import { resolveChartEngine, resolveChartType } from './echarts/chartTypeRegistry';

/**
 * Normalize a persisted report chart for ChartRenderer (camelCase + matrix combo fields).
 * @param {object} [summarizedData] — optional, used to infer line field from derived metrics
 */
function commonChartDisplayFields(chart) {
  return {
    scatterXAxis: chart.scatterXAxis || chart.scatter_x_axis,
    scatterYAxis: chart.scatterYAxis || chart.scatter_y_axis,
    scatterGroupField: chart.scatterGroupField || chart.scatter_group_field,
    scatterXAxisLabel: chart.scatterXAxisLabel || chart.scatter_x_axis_label,
    scatterYAxisLabel: chart.scatterYAxisLabel || chart.scatter_y_axis_label,
    pieValueField: chart.pieValueField || chart.pie_value_field,
    pieSliceField: chart.pieSliceField || chart.pie_slice_field,
    funnelStageField: chart.funnelStageField || chart.funnel_stage_field,
    funnelValueField: chart.funnelValueField || chart.funnel_value_field,
    legendPosition: chart.legendPosition || chart.legend_position || 'top',
    showTooltips:
      chart.showTooltips !== undefined
        ? chart.showTooltips
        : chart.show_tooltips !== undefined
          ? chart.show_tooltips
          : true,
    showLabels:
      chart.showLabels !== undefined
        ? chart.showLabels
        : chart.show_labels !== undefined
          ? chart.show_labels
          : true,
    labelColor: chart.labelColor || chart.label_color,
    xAxisLabel: chart.xAxisLabel || chart.x_axis_label,
    yAxisLabel: chart.yAxisLabel || chart.y_axis_label,
    yAxisLabelSecondary:
      chart.yAxisLabelSecondary || chart.y_axis_label_secondary,
    fieldColors: chart.fieldColors || chart.field_colors,
    legendColors: chart.legendColors || chart.legend_colors,
    showValues: chart.showValues ?? chart.show_values,
    showPercentages: chart.showPercentages ?? chart.show_percentages,
    // Default on for donut/funnel-style totals when unset (matches ReportChart defaults).
    showTotal:
      chart.showTotal !== undefined
        ? chart.showTotal
        : chart.show_total !== undefined
          ? chart.show_total
          : true,
    showMatrixColumnTotal:
      chart.showMatrixColumnTotal ?? chart.show_matrix_column_total,
    chartFullHeight: chart.chartFullHeight ?? chart.chart_full_height,
    stackTo100Percent: chart.stackTo100Percent ?? chart.stack_to_100_percent,
    derivedMetrics: chart.derivedMetrics || chart.derived_metrics,
    valueFieldType: chart.valueFieldType || chart.value_field_type,
  };
}

export function normalizeReportChartForDisplay(chart, summarizedData = null) {
  if (!chart) return chart;

  const chartType = resolveChartType(chart);
  const stackByFieldEarly = chart.stackByField || chart.stack_by_field || '';
  const isMatrixReport =
    chart.matrixChart === true ||
    chart.matrix_chart === true ||
    summarizedData?._metadata?.report_info?.report_type === 'matrix';
  const groupCount = summarizedData?._metadata?.report_info?.group_count ?? 0;
  const isStackedCombo =
    Boolean(stackByFieldEarly) && (isMatrixReport || groupCount >= 2);
  /** @deprecated Renamed to isStackedCombo; kept so matrix/stack chart paths do not throw during HMR. */
  const isMatrixCombo = isStackedCombo;

  const xAxisField =
    chart.xAxisField ||
    chart.x_axis_field ||
    chart.selectedCategoryField ||
    chart.selected_category_field ||
    '';
  const yAxisFields =
    chart.yAxisFields ||
    chart.y_axis_fields ||
    (chart.yAxisField || chart.y_axis_field ? [chart.yAxisField || chart.y_axis_field] : []);

  let matrixLineField =
    chart.matrixLineField || chart.matrix_line_field || '';
  let matrixMeasureField =
    chart.matrixMeasureField || chart.matrix_measure_field || '';
  const stackByField = stackByFieldEarly;
  const summarizedFieldsMeta = summarizedData?._metadata?.summarized_fields || {};

  if (chartType === 'mixed' && !isStackedCombo) {
    const yAxisFields =
      chart.yAxisFields ||
      chart.y_axis_fields ||
      (chart.yAxisField || chart.y_axis_field
        ? [chart.yAxisField || chart.y_axis_field]
        : []);
    return {
      ...chart,
      ...commonChartDisplayFields(chart),
      chart_type: 'mixed',
      chartType: 'mixed',
      chartEngine: resolveChartEngine(chart),
      xAxisField,
      selectedCategoryField: xAxisField,
      yAxisField: yAxisFields[0],
      yAxisFields,
      seriesTypes: { ...(chart.seriesTypes || chart.series_types || {}) },
    };
  }

  const yField = yAxisFields[0] || chart.yAxisField || chart.y_axis_field;
  const yMeta = yField ? summarizedFieldsMeta[yField] : null;

  const matrixChart =
    chart.matrixChart ?? chart.matrix_chart ?? isMatrixReport;

  // Stacked combo (matrix or summary): derived % metrics are line overlays; bars use count/sum.
  if (isStackedCombo && !matrixLineField && yMeta?.is_derived) {
    matrixLineField = yField;
    matrixMeasureField = matrixMeasureField || 'record_count';
  }

  if (
    isStackedCombo &&
    !matrixLineField &&
    stackByField &&
    summarizedData?._metadata?.summarized_fields
  ) {
    const barField = matrixMeasureField || yField;
    const derivedEntry = Object.entries(summarizedFieldsMeta).find(
      ([id, m]) =>
        m?.is_derived &&
        id !== barField &&
        (m.data_type === 'percent' ||
          m.derived_type === 'win_rate' ||
          /win\s*rate|lost\s*rate|%/i.test(m.field_label || ''))
    );
    if (derivedEntry) {
      matrixLineField = derivedEntry[0];
      if (!matrixMeasureField && barField && !summarizedFieldsMeta[barField]?.is_derived) {
        matrixMeasureField = barField;
      } else if (!matrixMeasureField) {
        matrixMeasureField = 'record_count';
      }
    }
  }

  if (
    isStackedCombo &&
    !matrixMeasureField &&
    matrixLineField &&
    yField &&
    yField !== matrixLineField
  ) {
    matrixMeasureField = yField;
  }

  const seriesTypes = {
    ...(chart.seriesTypes || chart.series_types || {}),
  };
  if (matrixLineField) {
    seriesTypes[matrixLineField] = 'line';
  }

  const resolvedType =
    matrixLineField && chartType !== 'mixed' ? 'mixed' : chartType;

  return {
    ...chart,
    ...commonChartDisplayFields(chart),
    chart_type: resolvedType,
    chartType: resolvedType,
    chartEngine: resolveChartEngine(chart),
    xAxisField,
    selectedCategoryField: xAxisField,
    yAxisField: isStackedCombo ? matrixMeasureField || yField : yField,
    yAxisFields:
      isStackedCombo && matrixMeasureField ? [matrixMeasureField] : yAxisFields,
    stackByField,
    matrixLineField,
    matrixMeasureField,
    matrixChart,
    seriesTypes,
  };
}

/**
 * Build a chart object for ChartRenderer from a dashboard/home widget row.
 */
export function transformWidgetChartForDisplay(widget, summarizedDataOption = null) {
  if (!widget) return null;

  const chartConfig = widget.chart_config || widget.selectedChart || {};
  const summarizedData =
    summarizedDataOption ??
    widget.summarizedData ??
    widget.dataset ??
    null;
  const chartType = resolveChartType({
    chartType: chartConfig.chartType || chartConfig.chart_type,
    chart_type: widget.chart_type,
  });

  return {
    id: widget.chart_id || chartConfig.id || widget.id,
    title: widget.name || chartConfig.title,
    ...normalizeReportChartForDisplay(
      {
        ...chartConfig,
        chartType,
        chart_type: chartType,
      },
      summarizedData
    ),
  };
}
